// src/components/job-engine/LineageGraph.tsx
//
// Interactive parent → children DAG for a job's lineage, backed by
// GET /v1/jobs/{jobId}/lineage. Renders with @xyflow/react and dagre
// auto-layout. Each node shows the job type, state-colored pill, and
// duration; clicking navigates to that job's detail page.
//
// CORR-C2.

import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
  type NodeMouseHandler,
} from "@xyflow/react";
import dagre from "dagre";
import { Spinner, MessageBar, MessageBarType, Stack, Text } from "@fluentui/react";
import "@xyflow/react/dist/style.css";

import { useJobLineage } from "./useJobLineage";
import StatePill from "./StatePill";
import type { JobLineageNode, JobState } from "@/api/jobEngine";

interface LineageGraphProps {
  rootJobId: string;
  /** Pixel height of the canvas. Defaults to 420 to match the surrounding card. */
  height?: number;
}

const NODE_WIDTH = 260;
const NODE_HEIGHT = 76;

// React Flow's Node<TData> generic constrains TData to Record<string, unknown>.
// Add the index signature so our typed lineage payload satisfies the constraint
// while still keeping all named properties strongly typed at use sites.
type NodeData = JobLineageNode & {
  isRoot: boolean;
  durationMs: number | null;
  [key: string]: unknown;
};

/**
 * Dagre layout pass — assigns (x, y) to each node so React Flow renders a
 * top-down DAG. The function returns a new array (immutable) keyed by id.
 */
const layout = (nodes: Node[], edges: Edge[]): Node[] => {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 24, ranksep: 60 });
  nodes.forEach((n) => g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const pos = g.node(n.id);
    return {
      ...n,
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
      // Tell React Flow this node is laid out (vs centered at origin).
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    };
  });
};

/**
 * Custom node renderer — Fluent-styled card with the job type, a state pill,
 * and a small "JobId · duration · attempts" line. The root node gets a thicker
 * blue border so the user can find it at a glance.
 */
const LineageNodeCard: React.FC<NodeProps<Node<NodeData>>> = ({ data }) => {
  const stateForPill: JobState = data.finalState ?? data.state;
  return (
    <div
      style={{
        padding: 10,
        border: data.isRoot ? "2px solid #0078d4" : "1px solid #d1d5db",
        borderRadius: 6,
        background: "#ffffff",
        width: NODE_WIDTH,
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        fontSize: 12,
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: "#605e5c" }} />
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <Text
          variant="small"
          styles={{
            root: {
              fontFamily: "'Cascadia Mono', Consolas, monospace",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 170,
            },
          }}
          title={data.jobType}
        >
          {data.jobType}
        </Text>
        <StatePill state={stateForPill} />
      </Stack>
      <Text variant="xSmall" styles={{ root: { color: "#605e5c", marginTop: 4, display: "block" } }}>
        {data.jobId.slice(0, 8)}…
        {data.durationMs != null && ` · ${data.durationMs} ms`}
        {data.attemptCount > 1 && ` · ${data.attemptCount} attempts`}
      </Text>
      <Handle type="source" position={Position.Bottom} style={{ background: "#605e5c" }} />
    </div>
  );
};

// Synthetic "trigger" node that floats above the root job — visualizes the
// origin of the lineage (API call, scheduled cron, workbench, etc.) without
// being navigable. Distinct purple pill so it's visually clear it's not a job.
type TriggerNodeData = {
  origin: string | null;
  [key: string]: unknown;
};

// Origin string → display label. "producer" is the JobEngine SDK's default
// (set in JobEngineClient.PublishAsync when the caller doesn't override it),
// so it does NOT mean "API" — it just means "no explicit origin was declared
// at publish time". Distinct values come from publishers that explicitly set
// options.ApplicationProperties["origin"] (e.g. RecurringDispatcherFunction
// sets "recurring").
const ORIGIN_LABELS: Record<string, string> = {
  api: "API",
  scheduled: "Scheduled",
  recurring: "Scheduled",
  workbench: "Workbench",
  test: "Test endpoint",
  producer: "Producer",
};

const ORIGIN_ICONS: Record<string, string> = {
  api: "🌐",
  scheduled: "⏰",
  recurring: "⏰",
  workbench: "🛠️",
  test: "🧪",
  producer: "📦",
};

const labelForOrigin = (origin: string | null): string => {
  if (!origin) return "Trigger";
  const key = origin.toLowerCase();
  return ORIGIN_LABELS[key] ?? origin;
};

const iconForOrigin = (origin: string | null): string => {
  if (!origin) return "⚡";
  return ORIGIN_ICONS[origin.toLowerCase()] ?? "⚡";
};

const TriggerNodeCard: React.FC<NodeProps<Node<TriggerNodeData>>> = ({ data }) => (
  <div
    style={{
      padding: "6px 14px",
      border: "2px solid #7c3aed",
      borderRadius: 999,
      background: "#f5f3ff",
      color: "#5b21b6",
      fontSize: 12,
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      cursor: "default",
      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
    }}
    title={data.origin ?? "Trigger"}
  >
    <span>{iconForOrigin(data.origin)}</span>
    <span>{labelForOrigin(data.origin)}</span>
    <Handle type="source" position={Position.Bottom} style={{ background: "#7c3aed" }} />
  </div>
);

const NODE_TYPES = { job: LineageNodeCard, trigger: TriggerNodeCard };

const LineageGraph: React.FC<LineageGraphProps> = ({ rootJobId, height = 420 }) => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useJobLineage(rootJobId);

  const { nodes, edges } = useMemo(() => {
    if (!data) return { nodes: [] as Node[], edges: [] as Edge[] };

    const baseNodes: Node[] = data.nodes.map((n) => ({
      id: n.jobId,
      type: "job",
      position: { x: 0, y: 0 },
      data: {
        ...n,
        isRoot: n.jobId === data.rootJobId,
        durationMs:
          n.startedAt && n.finishedAt
            ? Math.max(0, Date.parse(n.finishedAt) - Date.parse(n.startedAt))
            : null,
      } as NodeData,
    }));

    const baseEdges: Edge[] = data.nodes
      .filter((n) => n.parentJobId)
      .map((n) => ({
        id: `${n.parentJobId}->${n.jobId}`,
        source: n.parentJobId as string,
        target: n.jobId,
        animated: false,
      }));

    // Synthetic trigger node above the root — visualizes Origin (API,
    // Scheduled, Workbench, …) as a non-navigable pill. The edge from
    // trigger → root is dashed purple to read distinctly from the
    // parent→child solid edges.
    const root = data.nodes.find((n) => n.jobId === data.rootJobId);
    if (root) {
      const triggerId = `trigger-${data.rootJobId}`;
      baseNodes.push({
        id: triggerId,
        type: "trigger",
        position: { x: 0, y: 0 },
        data: { origin: root.origin } as TriggerNodeData,
        selectable: false,
        connectable: false,
        draggable: false,
      });
      baseEdges.push({
        id: `${triggerId}->${data.rootJobId}`,
        source: triggerId,
        target: data.rootJobId,
        animated: false,
        style: { stroke: "#7c3aed", strokeWidth: 1.5, strokeDasharray: "4 4" },
      });
    }

    return { nodes: layout(baseNodes, baseEdges), edges: baseEdges };
  }, [data]);

  const onNodeClick: NodeMouseHandler = useCallback(
    (_evt, node) => {
      // Synthetic trigger node — not a real job, no navigation target.
      if (node.type === "trigger") return;
      navigate(`/job-engine/jobs/${encodeURIComponent(node.id)}`);
    },
    [navigate],
  );

  if (isLoading) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner label="Loading lineage…" />
      </div>
    );
  }

  if (error) {
    return <MessageBar messageBarType={MessageBarType.error}>Couldn't load lineage — {error}.</MessageBar>;
  }

  if (!data || data.nodes.length === 0) {
    return (
      <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
        No lineage data for this job.
      </Text>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      {data.truncated && (
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            padding: "4px 10px",
            background: "#fef3c7",
            color: "#7c2d12",
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          }}
        >
          Lineage truncated — showing first {data.nodes.length} nodes
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        onNodeClick={onNodeClick}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#e5e7eb" gap={20} />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable />
      </ReactFlow>
    </div>
  );
};

export default LineageGraph;
